import { useMemo } from 'react';
import useStrategyDashboardStore from '../Stores/strategyDashboardStore';

import {
  formatCurrency,
  formatDate,
  formatPercent,
  formatTradeResult,
  parseNumber,
} from '../../services/strategyStats/strategyFormatters';

import '../Styles/ArgosStrategyPage.css';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatStructure(value) {
  if (!value) return 'ARGOS Position';

  return String(value)
    .replaceAll('_', ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function formatShortDate(value) {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function formatStrike(value, suffix = '') {
  if (value === null || value === undefined || value === '') return '—';

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return String(value);
  }

  return `${number.toLocaleString()}${suffix}`;
}

function formatSetupType(structure) {
  const normalized = String(structure || '').toLowerCase();

  if (normalized.includes('iron_condor')) return 'Short Iron Condor';
  if (normalized.includes('put_credit')) return 'Put Credit Spread';
  if (normalized.includes('call_credit')) return 'Call Credit Spread';
  if (normalized.includes('put_debit')) return 'Put Debit Spread';
  if (normalized.includes('call_debit')) return 'Call Debit Spread';

  return formatStructure(structure);
}

function getTradeStatus(trade) {
  return String(trade?.status || '').trim().toUpperCase();
}

function isClosedTrade(trade) {
  const status = getTradeStatus(trade);
  return status === 'CLOSED' || status === 'WIN' || status === 'LOSS';
}

function getTradePnl(trade) {
  return parseNumber(
    trade?.realized_pnl ??
      trade?.pnl ??
      trade?.raw_realized_pnl ??
      trade?.raw_pnl
  );
}

function getTradePremium(trade) {
  const credit = parseNumber(trade?.entry_credit);
  const debit = parseNumber(trade?.entry_debit);

  if (credit > 0) {
    return formatCurrency(credit, { maximumFractionDigits: 2 });
  }

  if (debit > 0) {
    return `-${formatCurrency(debit, { maximumFractionDigits: 2 })}`;
  }

  return '—';
}

function getTradeResult(trade) {
  const pnl = getTradePnl(trade);

  if (pnl > 0) return 'Win';
  if (pnl < 0) return 'Loss';

  return formatTradeResult(trade?.outcome || trade?.status);
}

function getTradeTone(trade) {
  const pnl = getTradePnl(trade);

  if (pnl > 0) return 'positive';
  if (pnl < 0) return 'negative';

  return 'neutral';
}

function buildRecentStats(recentTrades) {
  const closedTrades = recentTrades.filter(isClosedTrade).slice(0, 25);

  const pnlValues = closedTrades.map(getTradePnl);
  const totalPnl = pnlValues.reduce((sum, pnl) => sum + pnl, 0);

  const wins = pnlValues.filter(pnl => pnl > 0);
  const losses = pnlValues.filter(pnl => pnl < 0);

  const winRate =
    closedTrades.length > 0 ? (wins.length / closedTrades.length) * 100 : null;

  const avgWin =
    wins.length > 0
      ? wins.reduce((sum, pnl) => sum + pnl, 0) / wins.length
      : null;

  const avgLoss =
    losses.length > 0
      ? losses.reduce((sum, pnl) => sum + pnl, 0) / losses.length
      : null;

  return [
    {
      label: 'Recent P/L',
      value: formatCurrency(totalPnl, { showSign: true }),
      helper: `${closedTrades.length} closed trades`,
      tone: totalPnl > 0 ? 'positive' : totalPnl < 0 ? 'negative' : 'neutral',
    },
    {
      label: 'Win Rate',
      value: winRate === null ? '—' : formatPercent(winRate),
      helper: `${wins.length} wins / ${losses.length} losses`,
      tone: 'neutral',
    },
    {
      label: 'Avg Win',
      value: avgWin === null ? '—' : formatCurrency(avgWin, { showSign: true }),
      helper: 'Closed winners',
      tone: 'positive',
    },
    {
      label: 'Avg Loss',
      value: avgLoss === null ? '—' : formatCurrency(avgLoss, { showSign: true }),
      helper: 'Closed losers',
      tone: 'negative',
    },
  ];
}

function buildClosedTradeRows(recentTrades) {
  return recentTrades
    .filter(isClosedTrade)
    .slice(0, 10)
    .map(trade => ({
      id: trade.id,
      date: formatDate(trade.entry_date),
      expiration: formatDate(trade.expiration_date),

      // Use DB strategy_name, not structure_type
      structure: formatStructure(trade.strategy_name || trade.structure_type || trade.position_type),

      regime:
        trade.regime !== null && trade.regime !== undefined && trade.regime !== ''
          ? `Regime ${trade.regime}`
          : '—',

      environment: formatStructure(trade.environment),

      strikes: trade.strike_description || '—',
      credit: getTradePremium(trade),
      result: getTradeResult(trade),
      pnl: formatCurrency(getTradePnl(trade), { showSign: true }),
      tone: getTradeTone(trade),
      rawTrade: trade,
    }));
}

function buildOpenPositionRows(openPositions) {
  return openPositions.map(trade => ({
    id: trade.id,
    date: formatDate(trade.entry_date),
    expiration: formatDate(trade.expiration_date),

    // Use DB strategy_name, not structure_type
    structure: formatStructure(trade.strategy_name || trade.structure_type || trade.position_type),

    regime:
      trade.regime !== null && trade.regime !== undefined && trade.regime !== ''
        ? `Regime ${trade.regime}`
        : '—',

    environment: formatStructure(trade.environment),

    strikes: trade.strike_description || '—',
    credit: getTradePremium(trade),
    status: formatTradeResult(trade.status || 'Open'),
    pnl: 'Open',
    rawTrade: trade,
  }));
}

function getSignalText(signal) {
  if (!signal) return 'Waiting For Next Signal';

  if (signal.status === 'WAITING') {
    return 'Waiting For Next Signal';
  }

  return (
    signal.decision_reason ||
    signal.no_trade_reason ||
    signal.trade_title ||
    signal.status ||
    'Waiting For Next Signal'
  );
}

function StrategyStatCard({ label, value, helper, tone }) {
  return (
    <div className={`argosStatCard argosStatCard--${tone || 'neutral'}`}>
      <div className="argosStatLabel">{label}</div>
      <div className="argosStatValue">{value}</div>
      <div className="argosStatSubtext">{helper}</div>
    </div>
  );
}

function InsightCard({ label, title, children }) {
  return (
    <div className="argosInsightCard">
      <div className="argosInsightLabel">{label}</div>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}

function CurrentOpenTradesList({ rows }) {
  if (!rows.length) {
    return (
      <div className="argosCommandOpenEmpty">
        No active ARGOS positions.
      </div>
    );
  }

  return (
    <div className="argosCommandOpenTableWrap">
      <div className="argosCommandOpenTitle">
        <span>Open Positions</span>
        <small>Current active trades</small>
      </div>

      <table className="argosCommandOpenTable">
        <thead>
          <tr>
            <th>Strat</th>
            <th>Type</th>
            <th>Ent</th>
            <th>Setup</th>
            <th>Exp</th>
            <th>Cr</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((trade, index) => {
            const rawTrade = trade.rawTrade || {};

            return (
              <tr key={trade.id || `${trade.date}-${index}`}>
                <td>ARGOS</td>

                <td>{trade.structure}</td>

                <td>{formatShortDate(rawTrade.entry_date)}</td>

                <td>
                  <div className="argosCommandSetupGrid">
                    <div className="argosCommandSetupSide">
                      <span className="argosCommandSetupLabel">Calls</span>

                      <div className="argosCommandLegRow">
                        <span className="argosSellBubble">S</span>
                        <strong>{formatStrike(rawTrade.short_call, 'C')}</strong>
                      </div>

                      <div className="argosCommandLegRow">
                        <span className="argosBuyBubble">B</span>
                        <strong>{formatStrike(rawTrade.long_call, 'C')}</strong>
                      </div>
                    </div>

                    <div className="argosCommandSetupSide">
                      <span className="argosCommandSetupLabel">Puts</span>

                      <div className="argosCommandLegRow">
                        <span className="argosSellBubble">S</span>
                        <strong>{formatStrike(rawTrade.short_put, 'P')}</strong>
                      </div>

                      <div className="argosCommandLegRow">
                        <span className="argosBuyBubble">B</span>
                        <strong>{formatStrike(rawTrade.long_put, 'P')}</strong>
                      </div>
                    </div>
                  </div>
                </td>

                <td>{formatShortDate(rawTrade.expiration_date)}</td>
                <td>{trade.credit}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OpenPositionsTable({ rows }) {
  return (
    <section className="argosTablePanel">
      <div className="argosPanelHeader">
        <div>
          <div className="argosSectionEyebrow">Open Exposure</div>
          <h2>Open ARGOS Positions</h2>
        </div>
      </div>

      <div className="argosTableWrap">
        <table className="argosTradeTable">
          <thead>
            <tr>
              <th>Entry</th>
              <th>OPEX</th>
              <th>Structure</th>
              <th>Regime</th>
              <th>Environment</th>
              <th>Strikes</th>
              <th>Credit</th>
              <th>Status</th>
              <th>P/L</th>
            </tr>
          </thead>

          <tbody>
            {rows.length > 0 ? (
              rows.map((trade, index) => (
                <tr key={trade.id || `${trade.date}-${index}`}>
                  <td>{trade.date}</td>
                  <td>{trade.expiration}</td>
                  <td>{trade.structure}</td>
                  <td>{trade.regime}</td>
                  <td>{trade.environment}</td>
                  <td>{trade.strikes}</td>
                  <td>{trade.credit}</td>
                  <td>
                    <span className="argosStatusPill argosStatusPill--open">
                      {trade.status}
                    </span>
                  </td>
                  <td className="argosPnlCell argosPnlCell--neutral">
                    {trade.pnl}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="argosEmptyTableCell">
                  No open ARGOS positions available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RecentTradesTable({ rows }) {
  return (
    <section className="argosTablePanel">
      <div className="argosPanelHeader">
        <div>
          <div className="argosSectionEyebrow">Trade History</div>
          <h2>Latest 10 Closed Trades</h2>
        </div>
      </div>

      <div className="argosTableWrap">
        <table className="argosTradeTable">
          <thead>
            <tr>
              <th>Date</th>
              <th>Expiration</th>
              <th>Structure</th>
              <th>Regime</th>
              <th>Environment</th>
              <th>Strikes</th>
              <th>Credit</th>
              <th>Result</th>
              <th>P/L</th>
            </tr>
          </thead>

          <tbody>
            {rows.length > 0 ? (
              rows.map((trade, index) => (
                <tr key={trade.id || `${trade.date}-${index}`}>
                  <td>{trade.date}</td>
                  <td>{trade.expiration}</td>
                  <td>{trade.structure}</td>
                  <td>{trade.regime}</td>
                  <td>{trade.environment}</td>
                  <td>{trade.strikes}</td>
                  <td>{trade.credit}</td>
                  <td>
                    <span className={`argosStatusPill argosStatusPill--${trade.tone}`}>
                      {trade.result}
                    </span>
                  </td>
                  <td className={`argosPnlCell argosPnlCell--${trade.tone}`}>
                    {trade.pnl}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="argosEmptyTableCell">
                  No closed ARGOS trades available yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function ARGOSPage() {
  const argosPayload = useStrategyDashboardStore(
    state => state.strategies?.argos
  );

  const openPositions = useMemo(
    () => asArray(argosPayload?.open_positions),
    [argosPayload]
  );

  const recentTrades = useMemo(
    () => asArray(argosPayload?.recent_trades),
    [argosPayload]
  );

  const recentStats = useMemo(
    () => buildRecentStats(recentTrades),
    [recentTrades]
  );

  const openRows = useMemo(
    () => buildOpenPositionRows(openPositions),
    [openPositions]
  );

  const closedRows = useMemo(
    () => buildClosedTradeRows(recentTrades),
    [recentTrades]
  );

  const overview = argosPayload?.overview || {};
  const signal = argosPayload?.signal || null;

  const stats = [
    {
      label: 'Open Positions',
      value: String(openPositions.length),
      helper: 'Concurrent multi-day exposure',
      tone: 'gold',
    },
    ...recentStats,
  ];

  return (
    <main className="argosStrategyPage">
      <section className="argosHero">
        <div className="argosHeroContent">
          <div className="argosEyebrow">Adaptive Regime Guided Option Selling</div>

          <h1>ARGOS</h1>

          <h2>Multi-Day Adaptive Premium Engine</h2>

          <p>
            ARGOS is the multi-day aggressive growth layer of the SPX Gamma premium sellers suite.
            Rather than forcing growth through position sizing, ARGOS compounds through
            diversification, adaptive structure selection, longer expiration windows, and multiple
            defined-risk positions running at the same time.
          </p>

          <p>
            Each trade is opened only when market structure supports it. ARGOS classifies the
            market across bull, bear, neutral, unstable, bear exhaustion, and bull exhaustion
            environments, then uses gamma exposure, volatility pressure, regime behavior,
            options flow, and historical forward movement to select the structure, direction,
            and expiration window.
          </p>

          <p>
            To prevent capital over-allocation and reduce concentrated directional risk, ARGOS
            caps total open exposure at five active trades at any one time.
          </p>

          <div className="argosHeroAction">
            <a
              className="argosHeroFrameworkLink"
              href="https://spxgamma-argos-strategy-framework.spxgamma.com/"
              target="_blank"
              rel="noreferrer"
            >
              View Full ARGOS Framework
            </a>
          </div>
        </div>

        <div className="argosCommandCard">
          <div>
            <div className="argosCommandLabel">Current ARGOS Signal</div>
            <div className="argosCommandStatus">{getSignalText(signal)}</div>
          </div>

          <p>
            Current program status: <strong>{overview.status || '—'}</strong>.
            ARGOS waits for the setup instead of forcing daily trades.
          </p>

          <CurrentOpenTradesList rows={openRows} />
        </div>
      </section>

      <section className="argosStatsGrid">
        {stats.map(stat => (
          <StrategyStatCard
            key={stat.label}
            label={stat.label}
            value={stat.value}
            helper={stat.helper}
            tone={stat.tone}
          />
        ))}
      </section>

      <section className="argosNarrativeGrid">
        <InsightCard label="Growth Method" title="Aggressive Growth Without Oversizing">
          ARGOS is aggressive because it can stack multiple independent
          opportunities across time. A bullish spread, neutral condor, or
          reversal-based structure can coexist when each was opened under its own
          valid regime and forward distribution.
        </InsightCard>

        <InsightCard label="Decision Engine" title="Adaptive Structure Selection">
          ARGOS does not force one trade type. It classifies the market as bull,
          bear, neutral, unstable, bullish exhaustion, or bearish exhaustion,
          then selects the position type that best fits that environment.
        </InsightCard>

        <InsightCard label="Signal Quality" title="Market Structure First">
          Price alone is not enough. ARGOS reads dealer positioning, volatility
          pressure, regime behavior, and historical forward outcomes before a
          trade is allowed.
        </InsightCard>
      </section>

      <section className="argosProcessPanel">
        <div className="argosPanelHeader">
          <div>
            <div className="argosSectionEyebrow">ARGOS Decision Stack</div>
            <h2>How The Strategy Reads The Market</h2>
          </div>
        </div>

        <div className="argosProcessGrid">
          <div>
            <span>01</span>
            <h3>Options Flow</h3>
            <p>Identifies whether pressure is building higher, lower, or staying balanced.</p>
          </div>

          <div>
            <span>02</span>
            <h3>Gamma Exposure</h3>
            <p>Determines whether dealer structure is supportive, unstable, or transitional.</p>
          </div>

          <div>
            <span>03</span>
            <h3>Regime & Volatility</h3>
            <p>Classifies the environment before deciding if premium should be sold.</p>
          </div>

          <div>
            <span>04</span>
            <h3>Forward Distribution</h3>
            <p>Uses historical movement behavior to select structure, strikes, and duration.</p>
          </div>
        </div>
      </section>

      {/*<OpenPositionsTable rows={openRows} />*/}

      <RecentTradesTable rows={closedRows} />

      <section className="acceDisclaimer">
        ARGOS trade posts are for educational and informational purposes only.
        They are not financial advice or a recommendation to buy or sell any
        security or options contract. Options trading involves risk and may not
        be suitable for all traders.
      </section>
    </main>
  );
}